import { useState } from "react";
import { Package, Truck, CircleCheck as CheckCircle2, Clock } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { api, errCode } from "@/lib/api";
import StatusBadge from "@/components/store/StatusBadge";
import { Field } from "@/pages/Checkout";
import { ProductImage } from "@/components/store/ProductCard";
import { InvoiceButton } from "@/components/store/Invoice";

export function OrderView({ o }) {
  const { t, pick, money, date } = useI18n();
  const steps = ["processing", "shipped", "delivered"];
  const idx = steps.indexOf(o.status);
  const icons = [Package, Truck, CheckCircle2];
  return (
    <div className="space-y-5" data-testid="order-view">
      <div className="card p-5 sm:p-6 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><p className="text-xs muted">{t("payment.order_number")}</p><p className="font-display font-bold text-lg">{o.order_number}</p></div>
          <div className="flex gap-2"><StatusBadge status={o.status} group="order_status" testid="order-status" /><StatusBadge status={o.payment_status} group="payment_status" /></div>
        </div>
        {o.status !== "cancelled" && <div className="grid grid-cols-3 gap-2">{steps.map((s, i) => { const I = icons[i]; const on = i <= idx; return (
          <div key={s} className="text-center space-y-2"><div className={`w-11 h-11 mx-auto rounded-full flex items-center justify-center ${on ? "bg-[#2563EB] text-white" : "bg-[#F3F4F6] text-[#9CA3AF]"}`}><I className="w-5 h-5" /></div><p className={`text-xs font-medium ${on ? "" : "muted"}`}>{t(`order_status.${s}`)}</p></div>); })}</div>}
        {o.tracking?.tracking_number && <div data-testid="tracking-info" className="rounded-xl bg-[#F5F7FA] p-4 space-y-1 text-sm">
          <p><span className="muted">{t("track.number")}:</span> <b className="font-display">{o.tracking.tracking_number}</b></p>
          {o.tracking.carrier && <p><span className="muted">{t("track.carrier")}:</span> {o.tracking.carrier}</p>}
          <p><span className="muted">{t("track.last_update")}:</span> {date(o.tracking.last_update)}</p>
          {(o.tracking.events || []).slice().reverse().map((e, i) => <p key={i} className="flex gap-2 text-xs muted pt-1"><Clock className="w-3.5 h-3.5" />{date(e.at)} — {e.description}</p>)}
        </div>}
      </div>
      <div className="card p-5 sm:p-6 space-y-4">
        {o.items.map((i) => <div key={i.product_id + (i.sku || "")} className="flex gap-3 items-center"><div className="w-14 h-14 rounded-lg overflow-hidden shrink-0"><ProductImage path={i.image} alt="" /></div><div className="flex-1 text-sm"><p className="line-clamp-1">{pick(i.name)}</p><p className="muted text-xs">× {i.qty}</p></div><span className="text-sm font-medium">{money(i.line_total)}</span></div>)}
        <div className="border-t border-[#E5E7EB] pt-3 flex justify-between font-bold"><span>{t("checkout.total")}</span><span>{money(o.total)}</span></div>
        {o.refunded_total > 0 && <p className="text-sm text-[#2563EB]">{t("order.refunded_amount")}: {money(o.refunded_total)}</p>}
      </div>
    </div>
  );
}

export default function Track() {
  const { t } = useI18n();
  const [f, setF] = useState({ order_number: "", email: "" });
  const [o, setO] = useState(null);
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    try { const { data } = await api.post("/track", f); setO(data); } catch (e2) { setErr(errCode(e2)); setO(null); }
  };
  return (
    <div className="container-x py-12 max-w-2xl space-y-6" data-testid="track-page">
      <div className="space-y-2"><h1 className="text-3xl font-bold tracking-tight">{t("track.title")}</h1><p className="muted">{t("track.sub")}</p></div>
      <form onSubmit={submit} className="card p-5 sm:p-6 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
        <Field label={t("payment.order_number")} testid="track-order-input" required placeholder="ONEX-12345678" value={f.order_number} onChange={(e) => setF({ ...f, order_number: e.target.value })} />
        <Field label={t("checkout.email")} testid="track-email-input" required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <button data-testid="track-submit-btn" className="btn-primary">{t("track.submit")}</button>
      </form>
      {err && <p data-testid="track-error" className="text-sm text-[#DC2626]">{t(`errors.${err}`)}</p>}
      {o && <OrderView o={o} />}
      {o?.payment_status === "confirmed" && <InvoiceButton key={o.order_number} load={() => api.post("/track/invoice", f).then((r) => r.data)} />}
    </div>
  );
}
